import { GripVertical, Trash2 } from 'lucide-preact'
import type { JSX } from 'preact'
import type { Control, FieldValues, Path } from 'react-hook-form'
import { TextBoxField } from '@/components'

/**
 * One editable agenda item: reorder grip, position number, the bound text field, and delete.
 *
 * Bounds live in the parent. `onMoveUp` / `onMoveDown` are simply absent at the ends of the list, so
 * this component carries no index arithmetic and cannot move an item off either end — the failure
 * mode that would otherwise drop a row out of the array.
 */
export function AgendaRow<T extends FieldValues>({
  index,
  control,
  name,
  isDragOver,
  dragProps,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  index: number
  control: Control<T>
  name: Path<T>
  isDragOver: boolean
  dragProps: JSX.HTMLAttributes<HTMLDivElement>
  onDelete: () => void
  /** Absent when this is the first row. */
  onMoveUp?: () => void
  /** Absent when this is the last row. */
  onMoveDown?: () => void
}) {
  const handleGripKeyDown = (e: JSX.TargetedKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowUp' && onMoveUp) {
      e.preventDefault()
      onMoveUp()
    } else if (e.key === 'ArrowDown' && onMoveDown) {
      e.preventDefault()
      onMoveDown()
    }
  }

  const containerClasses = isDragOver
    ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
    : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900'

  return (
    <div
      className={`flex items-start gap-2 p-2 rounded-md border transition-colors ${containerClasses}`}
      {...dragProps}
    >
      <div className="flex items-center gap-1 pr-1 select-none">
        <button
          type="button"
          data-reorder-grip
          className="cursor-grab p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          tabIndex={0}
          aria-label={`Reorder agenda item ${index + 1}. Use Arrow Up / Arrow Down to move.`}
          onKeyDown={handleGripKeyDown}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 w-4 text-center">
          {index + 1}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <TextBoxField
            control={control}
            name={name}
            containerClassName="flex-1"
            maxLength={200}
            showCharCounter
            placeholder="Agenda item"
            rules={{ validate: v => (v ?? '').trim().length > 0 || 'Required' }}
          />
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 self-start mt-2"
            aria-label="Delete agenda item"
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    </div>
  )
}
